/**
 * AccessMap Filtering Utilities
 * 
 * Centralized filtering functions for tab-based access control.
 * These functions work with any entity type (projects, collections, etc.) that has an accessMap.
 * 
 * Tab Filtering Rules:
 * - myProjects/myItems: Items where user has ANY explicit role (OWNER, CONTRIBUTOR, or VIEWER)
 * - discovery: Items that are public (any role is PUBLIC) AND user has NO explicit role
 */

import { getUserRole, isPublic, hasExplicitRole } from './accessMapUtils';
import { ROLE_TYPES, ACCESS_TYPES, TAB_NAMES } from './accessMapConstants';

/**
 * Filters items to show only those where the user has ANY explicit role (OWNER, CONTRIBUTOR, or VIEWER)
 * Used for "My Projects" / "My Items" tab
 * 
 * @param {Array} items - Array of items (projects, collections, etc.) with accessMap property
 * @param {string} username - Current user's username
 * @param {Object} currentUser - Optional current user object with id and username
 * @returns {Array} - Filtered array of items where user has any explicit role
 */
export const filterByMyItems = (items, username, currentUser = null) => {
  if (!Array.isArray(items) || !username) return [];
  
  return items.filter(item => {
    const userRole = getUserRole(item.accessMap, username, currentUser);
    // Return true if user has any explicit role (OWNER, CONTRIBUTOR, or VIEWER)
    return userRole === ROLE_TYPES.OWNER || 
           userRole === ROLE_TYPES.CONTRIBUTOR || 
           userRole === ROLE_TYPES.VIEWER;
  });
};

/**
 * Filters items to show only those that are public (any role is PUBLIC) AND user has NO explicit role
 * Used for "Discovery" tab
 * 
 * @param {Array} items - Array of items (projects, collections, etc.) with accessMap property
 * @param {string} username - Current user's username (to check if user has explicit role)
 * @param {Object} currentUser - Optional current user object with id and username
 * @returns {Array} - Filtered array of public items where user has no explicit role
 */
export const filterByPublicItems = (items, username, currentUser = null) => {
  if (!Array.isArray(items)) return [];
  
  return items.filter(item => {
    // Item must be public (any role is PUBLIC)
    const roleAccessList = item.accessMap?.roleAccessList;
    if (!roleAccessList || typeof roleAccessList !== 'object') return false;
    
    const ownerAccess = String(roleAccessList[ROLE_TYPES.OWNER] || '').trim();
    const contributorAccess = String(roleAccessList[ROLE_TYPES.CONTRIBUTOR] || '').trim();
    const viewerAccess = String(roleAccessList[ROLE_TYPES.VIEWER] || '').trim();
    
    const isItemPublic = ownerAccess === ACCESS_TYPES.PUBLIC || 
                         contributorAccess === ACCESS_TYPES.PUBLIC || 
                         viewerAccess === ACCESS_TYPES.PUBLIC;
    
    if (!isItemPublic) return false;
    
    // User must NOT have any explicit role
    if (username) {
      const hasRole = hasExplicitRole(item.accessMap, username, currentUser);
      return !hasRole;
    }
    
    return true;
  });
};

/**
 * Filters items based on the active tab
 * This is a convenience function that routes to the appropriate filter based on tab name
 * 
 * @param {Array} items - Array of items (projects, collections, etc.) with accessMap property
 * @param {string} activeTab - Active tab name ('myProjects', 'discovery', etc.)
 * @param {string} username - Current user's username (required for myProjects and discovery tabs)
 * @param {Object} currentUser - Optional current user object with id and username
 * @returns {Array} - Filtered array of items based on tab
 */
export const filterByTab = (items, activeTab, username, currentUser = null) => {
  if (!Array.isArray(items)) return [];
  
  switch (activeTab) {
    case TAB_NAMES.MY_PROJECTS:
    case TAB_NAMES.MY_COLLECTIONS:
    case TAB_NAMES.MY_ITEMS:
      return filterByMyItems(items, username, currentUser);
    
    case TAB_NAMES.DISCOVERY:
      return filterByPublicItems(items, username, currentUser);
    
    default:
      // If tab is unknown, return empty array for safety
      return [];
  }
};

